import React, {
  type MouseEvent,
  type ReactElement,
  useMemo,
  useRef,
  useState,
  useEffect,
  useContext,
} from 'react';

import { IResultContext, useDfContext } from '../../context/df-context';
import CardLoginRequired from './card-login-required';
import { DOWNLOAD_TYPE } from '../../constants';
import { getTranslation } from '../../utils/config';
import useMediaQuery from '../../hooks/use-media-query';
import MobileCardView from './mobile-card-view';
import CardView from './card-view';
import CardExpanded from './card-expanded';
import slugify from 'slugify';
import { useGroupAttributes } from '../../hooks/use-attributes';
import { SET_EXPANDED_CARD } from '../../context/actions';
import type { IItem } from '../../types';
import { DfCategoriesContext } from '../../context/categories/df-categories-context';

export default function Card({
  id,
  cardData,
  categoryId,
  category,
  title,
  taggingProduct,
  text,
  expanded,
  childOrder,
  dfSubtitle,
}: {
  id: number;
  cardData: IItem[];
  categoryId: string;
  category: string;
  title: string;
  taggingProduct: Array<string>;
  text: string;
  expanded: boolean;
  childOrder: Array<number>;
  dfSubtitle?: string;
}): ReactElement {
  const isMobile = useMediaQuery('(max-width: 581px)');
  const {
    state: { term, attributes, loggedIn },
    dispatch,
  } = useDfContext<IResultContext>();
  const [forceLogin, setForceLogin] = useState(false);
  const { categories } = useContext(DfCategoriesContext);
  const preventDownload = useRef(!loggedIn);

  const loginRequired = useMemo(
    () => cardData.some((item) => item.type === DOWNLOAD_TYPE.LOGIN),
    [cardData],
  );

  const toggleExpanded = () => {
    dispatch({
      type: SET_EXPANDED_CARD,
      card: expanded ? undefined : id,
    });
  };

  const handleDownload = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!loginRequired) return;
    if (preventDownload.current) {
      // preventDefault outside of async function
      e.preventDefault();
      preventDownload.current = !loggedIn;
      // trigger click event after preventDefault in async function
      if (loggedIn) {
        (e.target as HTMLAnchorElement).click();
      } else {
        setForceLogin(true);
      }
    }
  };

  const categoryItem = categories[categoryId];
  const categorySlug = categoryItem
    ? `df-icon-fallback df-icon-${slugify(String(categories[categoryId].pimName), { lower: true })}`
    : 'df-icon-fallback';

  const groupAttributes = useGroupAttributes(cardData, attributes);

  const taggingProductPrioritized = useMemo(() => {
    if (!taggingProduct) return null;
    const maybeTaggedWithPriority = taggingProduct.find(
      (taggingItem) =>
        term &&
        term.split(' ').some((el) => el.toLowerCase().trim() === taggingItem.toLowerCase().trim()),
    );
    if (maybeTaggedWithPriority) {
      return maybeTaggedWithPriority;
    }

    return taggingProduct.length === 1 ? taggingProduct[0] : null;
  }, [taggingProduct, term]);

  const hasTaggingProduct = taggingProduct && taggingProduct.length > 1;
  const hasShowMore = cardData.length > 3 || !!text || hasTaggingProduct;

  useEffect(() => {
    if (loggedIn && forceLogin) {
      setForceLogin(false);
    }
  }, [loggedIn, forceLogin]);

  return (
    <div className="df-list-module-wrapper">
      <article
        className={`df-list-module ${categorySlug} ${expanded && hasShowMore ? 'is-selected' : ''}`}
      >
        <span className="df-list-module__download-type">{category}</span>
        <h3
          className={`${
            !dfSubtitle ? 'df-list-module__headline-grow' : ''
          } df-list-module__headline`}
        >
          {title}
        </h3>
        {(taggingProductPrioritized || loginRequired) && !isMobile && (
          <div className="df-list-module__details">
            <span className="df-list-module__valid-for">
              {taggingProductPrioritized && (
                <>{getTranslation('valid_for', taggingProductPrioritized)}</>
              )}
            </span>
            {loginRequired && (
              <span className="df-list-module__login-required">
                {getTranslation('login_required')}
              </span>
            )}
          </div>
        )}
        {!isMobile && dfSubtitle && (
          <div className="df-list-module__details">
            <span
              className="df-list-module__note-row df-list-module__note"
              dangerouslySetInnerHTML={{ __html: dfSubtitle }}
            />
          </div>
        )}
        {isMobile ? (
          <>
            <MobileCardView
              id={id}
              taggingProduct={taggingProduct}
              text={text}
              childOrder={childOrder}
              loginRequired={loginRequired}
              cardData={cardData}
              groupAttributes={groupAttributes}
              handleDownload={handleDownload}
              dfSubtitle={dfSubtitle}
            />
            {expanded && forceLogin && loginRequired && !loggedIn && (
              <CardLoginRequired handleClose={() => setForceLogin(false)} />
            )}
          </>
        ) : (
          <CardView
            id={id}
            expanded={expanded}
            loginRequired={loginRequired}
            cardData={cardData}
            handleDownload={handleDownload}
            slicedGroupAttributes={groupAttributes}
            toggleExpanded={toggleExpanded}
            showMoreButton={hasShowMore}
          />
        )}
      </article>
      {!isMobile && hasShowMore && expanded && (
        <CardExpanded
          taggingProduct={taggingProduct}
          text={text}
          groupAttributes={groupAttributes}
          cardData={cardData}
          loginRequired={loginRequired}
          handleDownload={handleDownload}
          toggleExpanded={toggleExpanded}
          groupId={id}
          isEmpty={!hasShowMore}
        />
      )}

      {forceLogin && (
        <CardLoginRequired
          handleClose={() => {
            setForceLogin(false);
          }}
        />
      )}
    </div>
  );
}
